import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, Send, ArrowLeft } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { createQuote } from '../lib/api';
import { Toaster, toast } from 'sonner';

const QuoteListPage = () => {
  const { requestList, removeFromRequest, clearRequest, language } = useApp();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    company_name: '', contact_name: '', email: '', destination_country: '', notes: '',
  });

  const es = language === 'es';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (requestList.length === 0) { toast.error(es ? 'No hay productos' : 'No products in quote'); return; }
    setSubmitting(true);
    try {
      const items = requestList.map(p => ({
        product_id: p.id, unique_id: p.unique_id, oem: p.oem, model: p.model,
        grade: p.grade || null, qty: 1, unit_price: p.sale_price || 0,
      }));
      const res = await createQuote({ ...form, items });
      toast.success(`${es ? 'Cotización enviada' : 'Quote submitted'}: ${res.data.quote_id}`);
      clearRequest();
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || (es ? 'Error al enviar' : 'Failed to submit'));
    } finally { setSubmitting(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen pt-24 pb-16" data-testid="quote-success">
        <Toaster position="top-right" theme="dark" />
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="card-dark p-12">
            <div className="w-16 h-16 bg-[#10B981]/20 text-[#10B981] flex items-center justify-center mx-auto mb-6">
              <Send className="w-8 h-8" />
            </div>
            <h1 className="text-3xl font-bold mb-4">{es ? 'Cotización Enviada' : 'Quote Request Submitted'}</h1>
            <p className="text-white/60 mb-8">{es ? 'Nuestro equipo revisará su solicitud y le responderá dentro de 1-2 días hábiles.' : 'Our team will review your request and get back to you within 1-2 business days.'}</p>
            <div className="flex gap-4 justify-center">
              <button onClick={() => navigate('/products')} className="btn-primary">{es ? 'Ver Productos' : 'Browse Products'}</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="quote-list-page">
      <Toaster position="top-right" theme="dark" />
      <div className="max-w-5xl mx-auto px-6">
        <button onClick={() => navigate('/products')} className="flex items-center gap-2 text-white/60 hover:text-white mb-6 text-sm">
          <ArrowLeft className="w-4 h-4" /> {es ? 'Volver a Productos' : 'Back to Products'}
        </button>
        <h1 className="text-3xl font-bold mb-8" data-testid="quote-title">{es ? 'Solicitud de Cotización' : 'Quote Request'}</h1>

        {requestList.length === 0 ? (
          <div className="card-dark p-12 text-center">
            <p className="text-white/60 mb-4">{es ? 'No hay productos en la cotización' : 'No products in your quote list'}</p>
            <button onClick={() => navigate('/products')} className="btn-primary">{es ? 'Ver Productos' : 'Browse Products'}</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Items */}
            <div className="lg:col-span-2 space-y-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white/60 text-sm">{requestList.length} {es ? 'productos' : 'items'}</span>
                <button onClick={clearRequest} className="text-red-400 hover:text-red-300 text-xs">{es ? 'Limpiar todo' : 'Clear all'}</button>
              </div>
              {requestList.map((item) => (
                <div key={item.id} className="card-dark p-4 flex items-center justify-between" data-testid={`quote-item-${item.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-[#00D4FF] text-xs">{item.unique_id}</span>
                      {item.grade && <span className="px-1.5 py-0.5 text-[10px] font-bold bg-[#10B981]/20 text-[#10B981]">{item.grade}</span>}
                    </div>
                    <p className="text-white/80 text-sm truncate">{item.oem} — {item.model}</p>
                  </div>
                  <div className="flex items-center gap-4 ml-4">
                    <span className="text-white/40 text-xs italic">{es ? 'Cotización pendiente' : 'Quote pending'}</span>
                    <button onClick={() => removeFromRequest(item.id)} className="text-red-400 hover:text-red-300 p-1" data-testid={`remove-quote-${item.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
              <div className="card-dark p-4 flex justify-between items-center border-t border-white/10 mt-4">
                <span className="text-white/60">{requestList.length} {es ? 'artículos seleccionados' : 'items selected'}</span>
                <span className="text-sm text-white/40 italic">{es ? 'Precios en su cotización personalizada' : 'Pricing in your custom quotation'}</span>
              </div>
            </div>

            {/* Form */}
            <div className="card-dark p-6">
              <h2 className="text-lg font-semibold mb-4">{es ? 'Datos de Contacto' : 'Contact Information'}</h2>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="quote-form">
                <input required placeholder={es ? 'Nombre de Empresa *' : 'Company Name *'} value={form.company_name} onChange={e => setForm(f => ({...f, company_name: e.target.value}))} className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 focus:border-[#00D4FF] text-white text-sm" data-testid="quote-company" />
                <input required placeholder={es ? 'Nombre de Contacto *' : 'Contact Name *'} value={form.contact_name} onChange={e => setForm(f => ({...f, contact_name: e.target.value}))} className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 focus:border-[#00D4FF] text-white text-sm" data-testid="quote-contact" />
                <input required type="email" placeholder={es ? 'Email *' : 'Email *'} value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 focus:border-[#00D4FF] text-white text-sm" data-testid="quote-email" />
                <input required placeholder={es ? 'País de Destino *' : 'Destination Country *'} value={form.destination_country} onChange={e => setForm(f => ({...f, destination_country: e.target.value}))} className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 focus:border-[#00D4FF] text-white text-sm" data-testid="quote-country" />
                <textarea placeholder={es ? 'Notas (opcional)' : 'Notes (optional)'} value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} rows={3} className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 focus:border-[#00D4FF] text-white text-sm" data-testid="quote-notes" />
                <button type="submit" disabled={submitting} className="w-full btn-primary flex items-center justify-center gap-2" data-testid="submit-quote-btn">
                  <Send className="w-4 h-4" /> {submitting ? (es ? 'Enviando...' : 'Submitting...') : (es ? 'Enviar Cotización' : 'Submit Quote Request')}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuoteListPage;
